// src/pages/purchase/DetalleCompra.js
// Detalle de compra: factura + impuestos + recepciones de stock

import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Table, Spinner, Alert } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import NavContainer from '../../components/Nav';
import OffCanvasNav from '../../components/OffCanvasNav';
import { getPurchaseDetail } from '../../services/purchaseApi';
import { getTaxes } from '../../services/taxApi';

const formatterCOP = new Intl.NumberFormat('es-CO', {
  style: 'currency',
  currency: 'COP',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0
});

const DetalleCompra = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [detail, setDetail] = useState(null);
  const [taxDefinitions, setTaxDefinitions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    const load = async () => {
      if (!id) return;
      setLoading(true);
      setErrorMsg('');
      const [detailRes, taxesRes] = await Promise.all([
        getPurchaseDetail(id),
        getTaxes({ scope: 'compra' })
      ]);
      if (detailRes.ok) setDetail(detailRes.data);
      else setErrorMsg('No se pudo cargar el detalle de la compra.');
      if (taxesRes?.ok) {
        const list = taxesRes.data?.message ?? taxesRes.data;
        setTaxDefinitions(Array.isArray(list) ? list : []);
      }
      setLoading(false);
    };
    load();
  }, [id]);

  if (loading) {
    return (
      <>
        <NavContainer />
        <OffCanvasNav />
        <Container fluid className="mt-3">
          <div className="d-flex justify-content-center py-5">
            <Spinner animation="border" />
          </div>
        </Container>
      </>
    );
  }

  if (errorMsg || !detail) {
    return (
      <>
        <NavContainer />
        <OffCanvasNav />
        <Container fluid className="mt-3">
          <Alert variant="danger">{errorMsg || 'Compra no encontrada.'}</Alert>
          <Button variant="outline-primary" onClick={() => navigate('/compras')}>
            Volver a compras
          </Button>
        </Container>
      </>
    );
  }

  const receipts = detail.stock_receipts || [];
  const taxes = detail.purchase_taxes || [];
  const getTaxRate = (t) => {
    const fromTax = t.tax_rate ?? t.rate;
    if (fromTax != null && fromTax !== '' && parseFloat(fromTax) !== 0) return parseFloat(fromTax);
    const def = taxDefinitions.find((d) => String(d.id_tax ?? d.id) === String(t.id_tax));
    const r = def?.rate ?? def?.tax_rate;
    return r != null && r !== '' ? parseFloat(r) : null;
  };

  return (
    <>
      <NavContainer />
      <OffCanvasNav />
      <Container fluid className="mt-3">
        <Row>
          <Col>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <Button variant="outline-secondary" onClick={() => navigate('/compras')}>
                ← Volver
              </Button>
              <Button variant="primary" onClick={() => navigate(`/stock/registrar?purchase=${detail.id_purchase}`)}>
                Registrar stock
              </Button>
            </div>

            <Card className="mb-3">
              <Card.Header><strong>Factura #{detail.id_purchase}</strong></Card.Header>
              <Card.Body>
                <Row className="g-3">
                  <Col xs={12} md={6}>
                    <strong>Fecha:</strong> {detail.purchase_date || '-'}
                  </Col>
                  <Col xs={12} md={6}>
                    <strong>Proveedor:</strong> {detail.supplier?.name || '-'}
                  </Col>
                  <Col xs={12} md={6}>
                    <strong>Referencia:</strong> {detail.reference_doc || '-'}
                  </Col>
                  <Col xs={12} md={6}>
                    <strong>Método de pago:</strong> {detail.payment_method?.method_name || '-'}
                  </Col>
                  <Col xs={12}>
                    <strong>Estado:</strong>{' '}
                    <span className={`badge ${detail.status === 'completo' ? 'bg-success' : 'bg-warning text-dark'}`}>
                      {detail.status || 'pendiente'}
                    </span>
                  </Col>
                  {detail.notes && (
                    <Col xs={12}>
                      <strong>Notas:</strong> {detail.notes}
                    </Col>
                  )}
                </Row>
              </Card.Body>
            </Card>

            <Card className="mb-3">
              <Card.Header><strong>Totales</strong></Card.Header>
              <Card.Body>
                <Row className="g-2">
                  <Col xs={12} md={6}>
                    Subtotal: {formatterCOP.format(detail.subtotal ?? 0)}
                  </Col>
                  <Col xs={12} md={6}>
                    Descuento: {formatterCOP.format(detail.total_discount ?? 0)}
                  </Col>
                  <Col xs={12} md={6}>
                    Impuestos: {formatterCOP.format(detail.total_tax ?? 0)}
                  </Col>
                  <Col xs={12} md={6}>
                    <strong>Total: {formatterCOP.format(detail.grand_total ?? 0)}</strong>
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            {taxes.length > 0 && (
              <Card className="mb-3">
                <Card.Header><strong>Impuestos</strong></Card.Header>
                <Card.Body className="p-0">
                  <Table striped bordered size="sm" className="mb-0">
                    <thead>
                      <tr>
                        <th>Impuesto</th>
                        <th>Tasa %</th>
                        <th>Valor</th>
                      </tr>
                    </thead>
                    <tbody>
                      {taxes.map((t) => {
                        const rateNum = getTaxRate(t);
                        const rateDisplay = rateNum != null ? (rateNum < 1 ? `${(rateNum * 100).toFixed(1)}%` : `${rateNum}%`) : '-';
                        return (
                          <tr key={t.id_tax}>
                            <td>{t.tax_description || t.description || '-'}</td>
                            <td>{rateDisplay}</td>
                            <td>{formatterCOP.format(t.tax_amount ?? 0)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                </Card.Body>
              </Card>
            )}

            <Card>
              <Card.Header className="d-flex justify-content-between align-items-center">
                <strong>Recepciones de stock</strong>
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={() => navigate(`/stock/registrar?purchase=${detail.id_purchase}`)}
                >
                  Registrar stock
                </Button>
              </Card.Header>
              <Card.Body>
                {receipts.length === 0 ? (
                  <p className="text-muted mb-0">
                    No hay recepciones de stock. Usa &quot;Registrar stock&quot; para agregar ítems.
                  </p>
                ) : (
                  <Table striped bordered hover size="sm">
                    <thead>
                      <tr>
                        <th>SKU</th>
                        <th>Descripción</th>
                        <th>Cantidad</th>
                        <th>Costo unit.</th>
                        <th>Descuento</th>
                        <th>Fecha</th>
                      </tr>
                    </thead>
                    <tbody>
                      {receipts.map((r) => (
                        <tr key={r.id_stock_receipt || `${r.id_item}-${r.receipt_date}`}>
                          <td>{r.item?.sku || '-'}</td>
                          <td>{r.item?.description || '-'}</td>
                          <td>{r.quantity ?? 0}</td>
                          <td>{formatterCOP.format(r.unit_cost ?? 0)}</td>
                          <td>{formatterCOP.format(r.discount_amount ?? 0)}</td>
                          <td>{r.receipt_date || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </>
  );
};

export default DetalleCompra;
